import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const GrowthEnginesService = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState('overview');

  const features = [
    {
      id: 'marketing',
      title: 'AI Marketing Automation',
      icon: 'campaign',
      description: 'Intelligent marketing campaigns that adapt in real-time based on customer behavior and market trends.',
      benefits: ['Personalized campaigns', 'A/B testing automation', 'Multi-channel orchestration', 'ROI optimization'],
      metrics: { conversion: '+150%', engagement: '+85%', roi: '400% ROI' }
    },
    {
      id: 'sales',
      title: 'Sales Intelligence',
      icon: 'trending_up',
      description: 'AI-powered sales insights that identify opportunities, predict outcomes, and optimize sales processes.',
      benefits: ['Lead scoring', 'Pipeline forecasting', 'Deal optimization', 'Performance analytics'],
      metrics: { accuracy: '92%', pipeline: '+60%', closure: '+35%' }
    },
    {
      id: 'customer',
      title: 'Customer Journey AI',
      icon: 'route',
      description: 'Map and optimize customer journeys with AI-driven insights and automated touchpoint optimization.',
      benefits: ['Journey mapping', 'Touchpoint optimization', 'Churn prediction', 'Lifetime value modeling'],
      metrics: { retention: '+45%', satisfaction: '4.8/5', ltv: '+120%' }
    },
    {
      id: 'analytics',
      title: 'Growth Analytics',
      icon: 'auto_graph',
      description: 'Comprehensive analytics platform that tracks, measures, and predicts growth across all business metrics.',
      benefits: ['Real-time dashboards', 'Predictive modeling', 'Cohort analysis', 'Attribution tracking'],
      metrics: { insights: '100+ metrics', accuracy: '95%', speed: 'Real-time' }
    }
  ];

  const growthStrategies = [
    {
      title: 'Acquisition Optimization',
      description: 'AI-driven customer acquisition with optimized targeting and conversion',
      icon: 'person_add',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Retention Enhancement',
      description: 'Predictive models to identify and prevent customer churn',
      icon: 'favorite',
      color: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Revenue Expansion',
      description: 'Upselling and cross-selling opportunities powered by AI',
      icon: 'attach_money',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Market Intelligence',
      description: 'Competitive analysis and market trend identification',
      icon: 'insights',
      color: 'from-purple-500 to-violet-500'
    }
  ];

  const useCases = [
    {
      title: 'SaaS Growth Acceleration',
      description: 'Optimize subscription growth with AI-powered user acquisition, onboarding, and retention strategies.',
      icon: 'cloud',
      industries: ['SaaS', 'Software', 'Subscription Services']
    },
    {
      title: 'E-commerce Optimization',
      description: 'Maximize online sales with personalized recommendations, dynamic pricing, and conversion optimization.',
      icon: 'shopping_bag',
      industries: ['E-commerce', 'Retail', 'Marketplace']
    },
    {
      title: 'B2B Sales Enhancement',
      description: 'Accelerate B2B sales cycles with lead intelligence, account-based marketing, and sales automation.',
      icon: 'business',
      industries: ['B2B Services', 'Enterprise Software', 'Consulting']
    },
    {
      title: 'Digital Marketing ROI',
      description: 'Maximize marketing spend efficiency with AI-optimized campaigns and attribution modeling.',
      icon: 'ads_click',
      industries: ['Digital Agencies', 'Marketing', 'Advertising']
    }
  ];

  const metrics = [
    { label: 'Revenue Growth', value: '+250%', icon: 'trending_up', color: 'text-green-400' },
    { label: 'Customer Acquisition', value: '+180%', icon: 'person_add', color: 'text-blue-400' },
    { label: 'Retention Rate', value: '95%', icon: 'favorite', color: 'text-pink-400' },
    { label: 'Marketing ROI', value: '450%', icon: 'campaign', color: 'text-purple-400' },
    { label: 'Sales Velocity', value: '+120%', icon: 'speed', color: 'text-orange-400' },
    { label: 'Customer LTV', value: '+300%', icon: 'attach_money', color: 'text-emerald-400' }
  ];

  const integrations = [
    { name: 'HubSpot', icon: 'hub', category: 'CRM' },
    { name: 'Salesforce', icon: 'business', category: 'Sales' },
    { name: 'Google Analytics', icon: 'analytics', category: 'Analytics' },
    { name: 'Facebook Ads', icon: 'ads_click', category: 'Advertising' },
    { name: 'Mailchimp', icon: 'email', category: 'Email Marketing' },
    { name: 'Stripe', icon: 'payment', category: 'Payments' }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-red-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-orange-400 text-3xl">auto_graph</span>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  Growth <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Engines</span>
                </h1>
                <p className="text-gray-400 text-lg mt-2">AI-Powered Business Growth Platform</p>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Supercharge your business growth with intelligent automation that optimizes marketing, sales, 
              and customer success to drive sustainable revenue expansion and market dominance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105">
                Accelerate Growth
              </button>
              <button className="border border-gray-600 hover:border-orange-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                View Success Stories
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Metrics */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Proven Growth Results</h2>
            <p className="text-gray-300 text-lg">Average improvements across our client portfolio</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((metric, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-orange-500/50 transition-all text-center">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <span className={`material-symbols-outlined ${metric.color} text-2xl`}>{metric.icon}</span>
                </div>
                <h3 className={`text-3xl font-bold ${metric.color} mb-2`}>{metric.value}</h3>
                <p className="text-gray-300 font-medium">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Growth Strategies */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Growth Strategy Framework</h2>
            <p className="text-gray-300 text-lg">Comprehensive approach to sustainable business growth</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {growthStrategies.map((strategy, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-orange-500/50 transition-all group">
                <div className={`w-12 h-12 bg-gradient-to-r ${strategy.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <span className="material-symbols-outlined text-white text-2xl">{strategy.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{strategy.title}</h3>
                <p className="text-gray-300 text-sm">{strategy.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Navigation */}
      <section className="py-12 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setSelectedFeature(feature.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                  selectedFeature === feature.id
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="material-symbols-outlined">{feature.icon}</span>
                <span className="font-medium">{feature.title}</span>
              </button>
            ))}
          </div>

          {/* Feature Details */}
          {features.map((feature) => (
            selectedFeature === feature.id && (
              <div key={feature.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-orange-400 text-2xl">{feature.icon}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg mb-6">{feature.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Core Features:</h4>
                      <ul className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-3 text-gray-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-red-400 rounded-full"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Impact Metrics</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(feature.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                          <span className="text-gray-400 capitalize">{key}</span>
                          <span className="text-orange-400 font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Growth Solutions by Industry</h2>
            <p className="text-gray-300 text-lg">Tailored growth strategies for different business models</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-orange-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-orange-400 text-2xl">{useCase.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.industries.map((industry, idx) => (
                    <span key={idx} className="bg-gradient-to-r from-orange-500/10 to-red-500/10 text-orange-400 px-3 py-1 rounded-full text-sm border border-orange-500/20">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Growth Stack Integrations</h2>
            <p className="text-gray-300 text-lg">Connect with your existing marketing and sales tools</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-all group">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-gradient-to-r group-hover:from-orange-500/20 group-hover:to-red-500/20 transition-all">
                  <span className="material-symbols-outlined text-orange-400">{integration.icon}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{integration.name}</h4>
                <p className="text-gray-400 text-xs">{integration.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500/10 to-red-500/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Unlock Exponential Growth</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join industry leaders who are already using AI to achieve unprecedented growth rates and market expansion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            >
              Start Growing Today
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-orange-500 hover:bg-gradient-to-r hover:from-orange-500 hover:to-red-500 text-orange-400 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Growth Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GrowthEnginesService;